import { useEffect, useMemo, useState } from "react";
import {
  AppButton,
  AppTable,
  AppTableCell,
  AppTableHeadCell,
  AppTableRow,
  PageHeader,
  RowNumberCell,
  SearchField,
  StatusChip,
  TABLE_ROWS_PER_PAGE_OPTIONS,
} from "../../components/ui";
import { Box, Stack, Tab, Tabs } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  ClioService,
  ClioTaskTypeResponse,
  ClioTasksInfo,
} from "../../api";
import { useClioUsers } from "../../components/shared/ClioSelects";
import ClioTaskInfoDrawer from "./ClioTaskInfoDialog";
import ClioTaskTypesTable from "./ClioTaskTypesTable";

const MySwal = withReactContent(Swal);
const DEFAULT_ROWS_PER_PAGE = 10;

const TABLE_COLUMNS = [
  { id: "id", label: "ID" },
  { id: "name", label: "Name" },
  { id: "taskType", label: "Task Type" },
  { id: "automation", label: "Automation" },
  { id: "dueDays", label: "Due Days" },
  { id: "description", label: "Description" },
  { id: "assignee", label: "Assignee" },
];

type TaskTab = "templates" | "types";
type TemplateSortKey = (typeof TABLE_COLUMNS)[number]["id"];
type DrawerState =
  | { mode: "create" }
  | { mode: "edit"; task: ClioTasksInfo }
  | null;

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

function TaskTemplatesPanel() {
  const [allTasks, setAllTasks] = useState<ClioTasksInfo[]>([]);
  const [taskTypeLabels, setTaskTypeLabels] = useState<Record<string, string>>(
    {}
  );
  /** Enum names present in TaskEnums — templates linked to these are used in automation. */
  const [automationTaskTypes, setAutomationTaskTypes] = useState<Set<string>>(
    () => new Set()
  );
  const { users } = useClioUsers(true);
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<TemplateSortKey>("name");
  const [sortAsc, setSortAsc] = useState(true);

  const isUsedInAutomation = (taskType?: ClioTasksInfo["taskType"]) =>
    taskType != null && automationTaskTypes.has(String(taskType));

  useEffect(() => {
    setLoading(true);
    Promise.all([
      ClioService.getApiClioTaskData(),
      ClioService.getApiClioManagedTaskTypes().catch(
        () => [] as ClioTaskTypeResponse[]
      ),
    ])
      .then(([tasks, types]) => {
        setAllTasks(tasks ?? []);
        const labels: Record<string, string> = {};
        const inAutomation = new Set<string>();
        for (const t of types ?? []) {
          if (t.enumName) {
            labels[t.enumName] = t.name
              ? `${t.name} (${t.id})`
              : String(t.id);
            if (t.inAppEnum) {
              inAutomation.add(t.enumName);
            }
          }
        }
        setTaskTypeLabels(labels);
        setAutomationTaskTypes(inAutomation);
      })
      .catch(() => {
        MySwal.fire({
          icon: "error",
          title: <p>Error loading tasks.</p>,
        });
        setAllTasks([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const usedTaskTypes = useMemo(
    () =>
      allTasks
        .map((t) => (t.taskType != null ? String(t.taskType) : ""))
        .filter(Boolean),
    [allTasks]
  );

  const filteredTasks = useMemo(() => {
    const lower = searchTerm.trim().toLowerCase();
    const filtered = !lower
      ? [...allTasks]
      : allTasks.filter((t) => {
          const typeLabel =
            taskTypeLabels[String(t.taskType ?? "")] ?? String(t.taskType ?? "");
          const automationLabel = isUsedInAutomation(t.taskType)
            ? "yes"
            : "no";
          return (
            t.name?.toLowerCase().includes(lower) ||
            (t.description ?? "").toLowerCase().includes(lower) ||
            t.assignee?.toLowerCase().includes(lower) ||
            typeLabel.toLowerCase().includes(lower) ||
            String(t.taskType ?? "").toLowerCase().includes(lower) ||
            automationLabel.includes(lower)
          );
        });

    filtered.sort((a, b) => {
      let left: unknown = a[sortBy as keyof ClioTasksInfo];
      let right: unknown = b[sortBy as keyof ClioTasksInfo];
      if (sortBy === "taskType") {
        left =
          taskTypeLabels[String(a.taskType ?? "")] ?? String(a.taskType ?? "");
        right =
          taskTypeLabels[String(b.taskType ?? "")] ?? String(b.taskType ?? "");
      } else if (sortBy === "automation") {
        left = isUsedInAutomation(a.taskType) ? 1 : 0;
        right = isUsedInAutomation(b.taskType) ? 1 : 0;
      }
      const result = compareValues(left, right);
      return sortAsc ? result : -result;
    });
    return filtered;
  }, [searchTerm, allTasks, sortBy, sortAsc, taskTypeLabels, automationTaskTypes]);

  const maxPage = Math.max(0, Math.ceil(filteredTasks.length / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, sortBy, sortAsc]);

  const pageRows = filteredTasks.slice(
    safePage * rowsPerPage,
    safePage * rowsPerPage + rowsPerPage
  );

  const handleRequestSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortAsc((prev) => !prev);
    } else {
      setSortBy(columnId as TemplateSortKey);
      setSortAsc(true);
    }
  };

  const handleSave = async (formTask: ClioTasksInfo) => {
    const payload: ClioTasksInfo = {
      name: formTask.name,
      dueDays: formTask.dueDays,
      description: formTask.description,
      assignee: formTask.assignee,
      isCalendarDate: formTask.isCalendarDate,
      taskType: formTask.taskType,
    };

    try {
      if (drawer?.mode === "create") {
        const created = await ClioService.postApiClioTaskData(payload);
        setAllTasks((prev) => [...prev, created]);
        MySwal.fire({
          icon: "success",
          title: <p>Task template created.</p>,
        });
      } else if (drawer?.mode === "edit" && formTask.id != null) {
        await ClioService.postApiClioTaskDataUpdate(formTask.id, payload);
        setAllTasks((prev) =>
          prev.map((task) =>
            task.id === formTask.id ? { ...task, ...payload, id: formTask.id } : task
          )
        );
        MySwal.fire({
          icon: "success",
          title: <p>Task template updated.</p>,
        });
      }
      setDrawer(null);
    } catch {
      MySwal.fire({
        icon: "error",
        title: (
          <p>
            {drawer?.mode === "create"
              ? "Error creating task template."
              : "Error updating task template."}
          </p>
        ),
      });
    }
  };

  const handleDelete = async (task: ClioTasksInfo) => {
    if (!task.id) return;

    const result = await MySwal.fire({
      icon: "warning",
      title: "Delete task template?",
      html: (
        <p>
          This will remove <strong>{task.name}</strong> from local automation
          templates. This cannot be undone.
        </p>
      ),
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d32f2f",
    });

    if (!result.isConfirmed) return;

    try {
      await ClioService.deleteApiClioTaskData(task.id);
      setAllTasks((prev) => prev.filter((t) => t.id !== task.id));
      MySwal.fire({
        icon: "success",
        title: <p>Task template deleted.</p>,
      });
    } catch {
      MySwal.fire({
        icon: "error",
        title: <p>Error deleting task template.</p>,
      });
    }
  };

  const formatTaskType = (taskType?: ClioTasksInfo["taskType"]) => {
    if (taskType == null) return "—";
    const key = String(taskType);
    return taskTypeLabels[key] ?? key;
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <SearchField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <AppButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDrawer({ mode: "create" })}
        >
          Add Task Template
        </AppButton>
      </Stack>

      <AppTable
        columns={TABLE_COLUMNS}
        showRowNumber
        sortBy={sortBy}
        sortAsc={sortAsc}
        onRequestSort={handleRequestSort}
        trailingHeadCells={
          <AppTableHeadCell align="right">Actions</AppTableHeadCell>
        }
        extraColumnCount={1}
        loading={loading}
        loadingMessage="Loading task templates…"
        isEmpty={!loading && filteredTasks.length === 0}
        emptyTitle="No tasks found"
        emptyDescription={
          searchTerm ? "Try adjusting your search." : undefined
        }
        pagination={{
          count: filteredTasks.length,
          page: safePage,
          rowsPerPage,
          rowsPerPageOptions: [...TABLE_ROWS_PER_PAGE_OPTIONS],
          onPageChange: setPage,
          onRowsPerPageChange: (next) => {
            setRowsPerPage(next);
            setPage(0);
          },
        }}
      >
        {pageRows.map((task, index) => (
          <AppTableRow key={task.id} hover>
            <RowNumberCell value={safePage * rowsPerPage + index + 1} />
            <AppTableCell>{task.id}</AppTableCell>
            <AppTableCell>{task.name}</AppTableCell>
            <AppTableCell>{formatTaskType(task.taskType)}</AppTableCell>
            <AppTableCell>
              {isUsedInAutomation(task.taskType) ? (
                <StatusChip label="Yes" tone="secondary" size="small" />
              ) : (
                <StatusChip
                  label="No"
                  tone="neutral"
                  variant="outlined"
                  size="small"
                />
              )}
            </AppTableCell>
            <AppTableCell>{task.dueDays}</AppTableCell>
            <AppTableCell
              sx={{
                maxWidth: 250,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {task.description}
            </AppTableCell>
            <AppTableCell>{task.assignee}</AppTableCell>
            <AppTableCell align="right">
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <AppButton
                  variant="outlined"
                  size="small"
                  onClick={() => setDrawer({ mode: "edit", task })}
                >
                  Edit
                </AppButton>
                <AppButton
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => void handleDelete(task)}
                >
                  Delete
                </AppButton>
              </Stack>
            </AppTableCell>
          </AppTableRow>
        ))}
      </AppTable>

      <ClioTaskInfoDrawer
        open={drawer != null}
        mode={drawer?.mode === "create" ? "create" : "edit"}
        task={drawer?.mode === "edit" ? drawer.task : null}
        users={users}
        usedTaskTypes={usedTaskTypes}
        onClose={() => setDrawer(null)}
        onSave={handleSave}
      />
    </Box>
  );
}

export default function ClioTaskDataUpdate() {
  const [tab, setTab] = useState<TaskTab>("templates");

  return (
    <Box sx={{ width: "100%" }}>
      <PageHeader
        title="Task Templates"
        subtitle="Update Clio task templates and manage task types."
      />

      <Tabs
        value={tab}
        onChange={(_, next: TaskTab) => setTab(next)}
        sx={{
          mb: 2.5,
          borderBottom: 1,
          borderColor: "divider",
          minHeight: 42,
          "& .MuiTab-root": {
            minHeight: 42,
            textTransform: "none",
            fontWeight: 600,
          },
        }}
      >
        <Tab value="templates" label="Task templates" />
        <Tab value="types" label="Task types" />
      </Tabs>

      {tab === "templates" ? <TaskTemplatesPanel /> : <ClioTaskTypesTable />}
    </Box>
  );
}
